/* eslint-disable @typescript-eslint/no-unused-expressions */
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FC, useCallback, useEffect, useState } from "react";
import { FilterChip } from "../../../../components/filters/FilterChip";
import { CompanyFilter } from "../../../../legacy/types/companyFilter";
import { useMatchDataProcessingStore } from "../../../../store/MatchDataProcessingStore";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { useFiltersContext } from "../FiltersContext";
import { FILTERS, FILTERS_KEYS } from "../constants";
import {
  clearGeographyFilterState,
  clearOpportunityTypeFilterState,
  clearRangeFilterState,
  clearTargetOrganizationsFilterState,
  clearUploadedAccountsFilterState,
} from "../utils";
import { useFiltersChipLabelMap } from "./hooks/useFiltersChipLabelMap";
import { OpportunityTypeFilterChip } from "../../../../components/filters/OpportunityType/OpportunityTypeFilterChip";

export const FiltersChips: FC = () => {
  const {
    handleCancelApplyFilters,
    handleApplyFilters,
    setDraftFilters,
    handleDeleteFilter,
    draftFilters,
  } = useFiltersContext();

  const setUserFilters = useWorkspaceStore((state) => state.setUserFilters);
  const additionalFilters = useWorkspaceStore((state) => state.userFilters);
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);

  // Check if uploaded accounts is being processed
  const uploadedAccountsProcessing = useMatchDataProcessingStore((state) =>
    workspaceId
      ? state.getProcessingFile(workspaceId, "uploadedAccounts")
      : null
  );
  const stopProcessing = useMatchDataProcessingStore(
    (state) => state.stopProcessing
  );

  const [filters, setFilters] = useState(additionalFilters);

  const filtersChipLabelMap = useFiltersChipLabelMap({
    filters: additionalFilters,
    draftFilters,
    uploadedAccountsProcessing,
  });

  const handleCancelFilter = useCallback(
    (filterKey: string) => {
      /*
       * Cannot apply the same cancel behavior for company match
       * because the company match is processed in the background
       * and is not persisted to the additional filters yet
       */
      if (filterKey === FILTERS_KEYS.companyMatch) {
        return;
      }

      handleCancelApplyFilters();
    },
    [handleCancelApplyFilters]
  );

  const handleClearFilter = (filterKey: string) => {
    if (filterKey === FILTERS_KEYS.opportunityType) {
      setDraftFilters(clearOpportunityTypeFilterState);
      return;
    }

    if (filterKey === FILTERS_KEYS.geography) {
      setDraftFilters(clearGeographyFilterState);
      return;
    }

    if (filterKey === FILTERS_KEYS.targetOrganizations) {
      setDraftFilters(clearTargetOrganizationsFilterState);
      return;
    }

    if (filterKey === FILTERS_KEYS.companyMatch) {
      // Stop processing if file is being processed
      if (uploadedAccountsProcessing && workspaceId) {
        stopProcessing(workspaceId, "uploadedAccounts");
      }
      setDraftFilters(clearUploadedAccountsFilterState);
      setUserFilters(clearUploadedAccountsFilterState(additionalFilters));
      return;
    }

    if ([FILTERS_KEYS.employees, FILTERS_KEYS.revenue].includes(filterKey)) {
      const fixedFilterKey =
        filterKey === FILTERS_KEYS.employees
          ? "hasFixedEmployees"
          : "hasFixedRevenue";

      setDraftFilters((prevDraftFilters) =>
        clearRangeFilterState(filterKey, fixedFilterKey, prevDraftFilters)
      );
      return;
    }

    /**
     * filterKey will match the rest of the CompanyFilter keys
     */
    setDraftFilters((prevDraftFilters) => ({
      ...prevDraftFilters,
      [filterKey]: undefined,
    }));
  };

  const handleDelete = (filterKey: string) => {
    if (filterKey === FILTERS_KEYS.opportunityType) {
      setFilters(clearOpportunityTypeFilterState);
    } else if (filterKey === FILTERS_KEYS.targetOrganizations) {
      setFilters(clearTargetOrganizationsFilterState);
    } else if (filterKey === FILTERS_KEYS.geography) {
      setFilters(clearGeographyFilterState);
    } else if (
      [FILTERS_KEYS.employees, FILTERS_KEYS.revenue].includes(filterKey)
    ) {
      const fixedFilterKey =
        filterKey === FILTERS_KEYS.employees
          ? "hasFixedEmployees"
          : "hasFixedRevenue";

      setFilters((prevDraftFilters) =>
        clearRangeFilterState(filterKey, fixedFilterKey, prevDraftFilters)
      );
    } else if (filterKey === FILTERS_KEYS.companyMatch) {
      // Stop processing if file is being processed
      if (uploadedAccountsProcessing && workspaceId) {
        stopProcessing(workspaceId, "uploadedAccounts");
      }
      setDraftFilters(clearUploadedAccountsFilterState);
      setFilters(clearUploadedAccountsFilterState);
    } else {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [filterKey]: undefined,
      }));
    }

    /**
     * The chip removal and additional filters update conflicts each other
     * the chip does no remove until the additional filter is updated,
     * this is a workaround that allows the chip to be removed first
     */
    setTimeout(() => handleDeleteFilter(filterKey), 0);
  };

  const visibleChips = FILTERS.filter(({ filterKey }) => {
    if (!filters) return false;

    if (filterKey === FILTERS_KEYS.companyMatch) {
      return (
        draftFilters?.companyMatch?.fileId ||
        filters.companyMatch?.fileId ||
        uploadedAccountsProcessing?.fileId
      );
    }

    if (filterKey === FILTERS_KEYS.opportunityType) {
      return filters.opportunityType?.type;
    }

    if (filterKey === FILTERS_KEYS.geography) {
      return (
        filters.countryAlpha2s?.length ||
        filters.excludedCountryAlpha2s?.length ||
        filters.regionNames?.length ||
        filters.stateIds?.length
      );
    }

    if (filterKey === FILTERS_KEYS.targetOrganizations) {
      return (
        filters.hgIds?.length ||
        filters.hgIdsIncludingCorporateRelatives?.length
      );
    }

    if (filterKey === FILTERS_KEYS.companyType) {
      return filters.isGhq || filters.isGp || filters.isDp;
    }

    /**
     * filterKey will match the rest of the CompanyFilter keys
     */
    return filters[filterKey as keyof CompanyFilter];
  });

  useEffect(() => {
    setFilters(additionalFilters);
  }, [additionalFilters]);

  return (
    <>
      {visibleChips.map((chip, index) => {
        // Use dedicated FilterChip for OpportunityType
        if (chip.filterKey === FILTERS_KEYS.opportunityType) {
          return (
            <OpportunityTypeFilterChip
              key={`${chip.filterKey}-${index}`}
              id={`filter-chip-${chip.filterKey}`}
              showTitle={false}
              onDelete={() => {
                chip.filterKey && handleDelete(chip.filterKey);
              }}
            />
          );
        }

        // Use generic FilterChip for other filters
        return (
          <FilterChip
            id={`filter-chip-${chip.filterKey}`}
            key={`${chip.filterKey}-${index}`}
            label={chip.label}
            getSelectedCount={() => 1}
            onDelete={() => {
              chip.filterKey && handleDelete(chip.filterKey);
            }}
            renderLabel={() => {
              return (
                <Stack flexDirection="row" gap="3px">
                  <Typography color="secondary">{chip.label}:</Typography>
                  <Typography>{filtersChipLabelMap[chip.filterKey]}</Typography>
                </Stack>
              );
            }}
            popoverOptions={{
              onClose: () => handleCancelFilter(chip.filterKey),
              onClearButtonClick: () => {
                chip.filterKey && handleClearFilter(chip.filterKey);
              },
              onCancelButtonClick: () => handleCancelFilter(chip.filterKey),
              onApplyButtonClick: handleApplyFilters,
            }}
            paperStyles={{
              ...(chip.filterKey === "products" ? { width: 500 } : {}),
            }}
            addTitleCancelButton
          >
            <Stack direction="column" spacing={2}>
              {chip.content}
            </Stack>
          </FilterChip>
        );
      })}
    </>
  );
};
